import { useQuery, gql } from '@apollo/client';
import snakeCaseKeys from 'snakecase-keys';
import { take } from 'ramda';
import { useCountry } from '../../components/CountrySelector';
import { VARIANT_CELL_FRAGMENT } from '../../components/Grid/GridCell/VariantCell/VariantCell';
import { TRUNKSHOW_CELL_FRAGMENT } from '../../components/Grid/GridCell/TrunkshowCell/TrunkshowCell';
import { compact } from '../../lib/compact';
import {
  VariantsQuery,
  VariantsQuery_variants as Variant,
  VariantsQueryVariables
} from '../../generated/types/VariantsQuery';
import {
  TrunkshowsQuery,
  TrunkshowsQuery_trunkshows_trunkshows_edges_node as Trunkshow,
  TrunkshowsQueryVariables
} from '../../generated/types/TrunkshowsQuery';
import { useSegmentAnonymousId } from '../useSegmentAnonymousId';
import { usePreferences } from '../usePreferences';
import { useUser } from '../useUser';
import {
  ALGORITHM_QUERY,
  AlgorithmQueryData,
  AlgorithmQueryVars,
  GetLookVariantsQueryVariables,
  GetLookVariantsQuery,
  GET_LOOK_VARIANTS_QUERY
} from './recommendations.rest';

export interface Product {
  gender?: number;
  subcategory?: (string | null)[];
  productId?: string;
  variantId?: string;
}

export const VARIANTS_QUERY = gql`
  query VariantsQuery($ids: [String!], $productIds: [String!], $countryCode: String) {
    variants(ids: $ids, product_ids: $productIds) {
      ...VariantCellFragment
    }
  }
  ${VARIANT_CELL_FRAGMENT}
`;

export const TRUNKSHOWS_QUERY = gql`
  query TrunkshowsQuery(
    $designerIds: [String]
    $vertical: Gender
    $excludedCategories: [String]
    $singleDesignerOnly: Boolean
    $limit: Int
  ) {
    trunkshows(
      input: {
        designer_ids: $designerIds
        gender: $vertical
        excluded_categories: $excludedCategories
        single_designer_only: $singleDesignerOnly
      }
    ) {
      trunkshows(first: $limit) {
        edges {
          node {
            ...TrunkshowCellFragment
            categories
            limited_designer_data {
              id
            }
          }
        }
      }
    }
  }
  ${TRUNKSHOW_CELL_FRAGMENT}
`;

const DEFAULT_LIMIT = 40;

export const useVariantsQuery = (
  variantIds?: string[] | null,
  productIds?: string[] | null,
  limit = DEFAULT_LIMIT
) => {
  const {
    country: { alpha3Code }
  } = useCountry();

  return useQuery<VariantsQuery, VariantsQueryVariables>(VARIANTS_QUERY, {
    variables: {
      ids: take(limit, compact(variantIds)),
      productIds: take(limit, compact(productIds)),
      countryCode: alpha3Code
    },
    skip: (!variantIds || variantIds.length === 0) && (!productIds || productIds.length === 0),
    ssr: false
  });
};

export const useTrunkshowsQuery = ({
  designerIds,
  vertical,
  excludedCategories,
  singleDesignerOnly,
  limit
}: TrunkshowsQueryVariables) => {
  const { data, loading, error } = useQuery<TrunkshowsQuery, TrunkshowsQueryVariables>(
    TRUNKSHOWS_QUERY,
    {
      variables: {
        designerIds,
        vertical,
        excludedCategories,
        singleDesignerOnly,
        limit
      },
      skip: (designerIds || []).length === 0,
      ssr: false
    }
  );

  return {
    trunkshows:
      (data?.trunkshows?.trunkshows?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean) as Trunkshow[]) || [],
    loading,
    error
  };
};

export const useAlgorithmQuery = (
  id: string | null | undefined,
  attributes: { [key: string]: string | number | null | undefined }
) => {
  return useQuery<AlgorithmQueryData, AlgorithmQueryVars>(ALGORITHM_QUERY, {
    variables: {
      id: id || '',
      input: {
        data: {
          type: 'algorithms',
          attributes: snakeCaseKeys(attributes) as {
            [key: string]: string | number | null | undefined;
          }
        }
      }
    },
    skip: !id,
    ssr: false
  });
};

export const useVariantsAlgorithmQuery = (
  id: string | null | undefined,
  attributes: { [key: string]: string | number | null | undefined }
) => {
  const algorithmQuery = useAlgorithmQuery(id, attributes);

  const variantIds =
    algorithmQuery.data?.algorithm.data.attributes.entity_type === 'variants'
      ? algorithmQuery.data?.algorithm.data.attributes.entity_ids
      : [];
  const productIds =
    algorithmQuery.data?.algorithm.data.attributes.entity_type === 'products'
      ? algorithmQuery.data?.algorithm.data.attributes.entity_ids
      : [];

  const variantsQuery = useVariantsQuery(variantIds, productIds);

  return {
    variants: (variantsQuery.data?.variants?.filter(Boolean) as Variant[]) || [],
    loading: algorithmQuery.loading || variantsQuery.loading,
    error: algorithmQuery.error || variantsQuery.error
  };
};

export const useRecentlyViewed = () => {
  const { user } = useUser();
  const segmentId = useSegmentAnonymousId();

  const result = useVariantsAlgorithmQuery(segmentId, {
    algorithmType: 'recently_viewed',
    userId: user.id
  });

  if (!segmentId) {
    return {
      variants: [],
      loading: false,
      error: undefined
    };
  }

  return result;
};

export const useFavorites = () => {
  const { user } = useUser();

  return useVariantsAlgorithmQuery(user.id, { algorithmType: 'favorites' });
};

export const useSyte = (variantId?: string | null) => {
  return useVariantsAlgorithmQuery(variantId, { algorithmType: 'syte' });
};

export const useSimilarProducts = (product?: Product) => {
  return useVariantsAlgorithmQuery(product?.productId, {
    algorithmType: 'similar_products',
    subcategory: product?.subcategory?.[0],
    gender: product?.gender
  });
};

export const useLookVariants = (variantId?: string | null) => {
  const { country } = useCountry();
  const lookVariantsQuery = useQuery<GetLookVariantsQuery, GetLookVariantsQueryVariables>(
    GET_LOOK_VARIANTS_QUERY,
    {
      variables: { variantId: variantId || '', countryCode: country.alpha2Code },
      skip: !variantId,
      ssr: false
    }
  );
  const variantsQuery = useVariantsQuery(
    lookVariantsQuery.data?.getLookVariants.data?.map(
      recommendation => recommendation.attributes.variantId
    )
  );

  return {
    variants: (variantsQuery.data?.variants as Variant[]) || [],
    loading: lookVariantsQuery.loading || variantsQuery.loading,
    error: lookVariantsQuery.error || variantsQuery.error
  };
};

export const useTopTrunkshows = (affinityType: string | null = 'direct') => {
  const { user } = useUser();
  const { vertical } = usePreferences();

  const algorithmQuery = useAlgorithmQuery(user.id || '0', {
    algorithmType: 'designer_affinities',
    affinityType,
    gender: vertical
  });

  const trunkshowsQuery = useTrunkshowsQuery({
    designerIds: algorithmQuery.data?.algorithm.data.attributes.entity_ids,
    vertical,
    excludedCategories: ['Bridal'],
    singleDesignerOnly: true,
    limit: 16
  });

  return {
    trunkshows: trunkshowsQuery.trunkshows,
    loading: algorithmQuery.loading || trunkshowsQuery.loading,
    error: algorithmQuery.error || trunkshowsQuery.error
  };
};

export const useDesignerAffinities = (affinityType: string | null = 'direct') => {
  const { user } = useUser();
  const { vertical } = usePreferences();

  const algorithmQuery = useAlgorithmQuery(user.id, {
    algorithmType: 'designer_affinities',
    affinityType,
    gender: vertical
  });

  const trunkshowsQuery = useTrunkshowsQuery({
    designerIds: algorithmQuery.data?.algorithm.data.attributes.entity_ids,
    vertical,
    excludedCategories: ['Bridal'],
    singleDesignerOnly: true,
    limit: 16
  });

  return {
    trunkshows: trunkshowsQuery.trunkshows,
    loading: algorithmQuery.loading || trunkshowsQuery.loading,
    error: algorithmQuery.error || trunkshowsQuery.error
  };
};
